/** Every route path in one place. */
export const paths = {
  home: '/',

  // Public
  login: '/login',
  registerStudent: '/register/student',
  registerCompany: '/register/company',
  verifyEmail: '/verify-email',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  internships: '/internships',
  blog: '/blog',
  about: '/about',
  forStudents: '/for-students',
  forCompanies: '/for-companies',
  internshipDetail: (identifier = ':identifier') => `/internships/${identifier}`,

  // Student area
  student: {
    root: '/student',
    dashboard: '/student/dashboard',
    profile: '/student/profile',
    education: '/student/education',
    experience: '/student/experience',
    skills: '/student/skills',
    career: '/student/career',
    cv: '/student/cv',
    messages: '/student/messages',
    cvReview: (id = ':id') => `/student/cv/${id}/review`,
    cvFeedback: (id = ':id') => `/student/cv/${id}/feedback`,
    // Saved, For you and Applied are tabs of one "My internships" page.
    internships: '/student/internships',
    saved: '/student/internships/saved',
    recommendations: '/student/internships/for-you',
    applications: '/student/internships/applied',
    // Where those tabs used to live: notifications and bookmarks still point
    // here, and the router redirects them.
    legacy: {
      saved: '/student/saved',
      recommendations: '/student/for-you',
      applications: '/student/applications',
    },
    applicationDetail: (id = ':id') => `/student/applications/${id}`,
    interviews: '/student/interviews',
    offers: '/student/offers',
    insights: '/student/insights',
    account: '/student/account',
    invite: '/student/invite',
    plan: '/student/plan',
  },

  // Shared by every signed-in role
  notifications: '/notifications',

  // Company workspace
  company: {
    root: '/company',
    dashboard: '/company/dashboard',
    profile: '/company/profile',
    branding: '/company/branding',
    team: '/company/team',
    roles: '/company/team/roles',
    access: '/company/access',
    verification: '/company/verification',
    verificationGuide: '/company/verification/guide',
    internships: '/company/internships',
    internshipNew: '/company/internships/new',
    internshipDetail: (id = ':id') => `/company/internships/${id}`,
    internshipEdit: (id = ':id') => `/company/internships/${id}/edit`,
    internshipSourcing: (id = ':id') => `/company/internships/${id}/candidates`,
    internshipPerformance: (id = ':id') => `/company/internships/${id}/performance`,
    applications: '/company/applications',
    applicationDetail: (id = ':id') => `/company/applications/${id}`,
    candidates: '/company/candidates',
    talentPool: '/company/talent-pool',
    candidateDetail: (id = ':id') => `/company/candidates/${id}`,
    interviews: '/company/interviews',
    interviewCalendar: '/company/interview-calendar',
    offers: '/company/offers',
    analytics: '/company/analytics',
    recommendations: '/company/recommendations',
    billing: '/company/billing',
    messages: '/company/messages',
    integrations: '/company/integrations',
  },
  companyPublic: (slug = ':slug') => `/companies/${slug}`,
  // A student's shared portfolio: link-only, never listed.
  publicPortfolio: (slug = ':slug') => `/p/${slug}`,

  // Admin area
  admin: {
    root: '/admin',
    // Outside the admin guard: the one admin page reachable signed out.
    login: '/admin/login',
    overview: '/admin/overview',
    profile: '/admin/profile',
    verifications: '/admin/verifications',
    studentVerifications: '/admin/verifications/students',
    documentVerifications: '/admin/verifications/documents',
    internships: '/admin/internships',
    internshipDetail: (id = ':id') => `/admin/internships/${id}`,
    companies: '/admin/companies',
    companyDetail: (id = ':id') => `/admin/companies/${id}`,
    students: '/admin/students',
    studentDetail: (id = ':id') => `/admin/students/${id}`,
    recruiters: '/admin/recruiters',
    recruiterDetail: (id = ':id') => `/admin/recruiters/${id}`,
    recruitment: '/admin/recruitment',
    users: '/admin/users',
    applications: '/admin/applications',
    applicationDetail: (id = ':id') => `/admin/applications/${id}`,
    reports: '/admin/reports',
    audit: '/admin/audit',
    analytics: '/admin/analytics',
    revenueReport: '/admin/reports/revenue',
    verificationReport: '/admin/reports/verification',
    aiReport: '/admin/reports/ai',
    billing: '/admin/billing',
    plans: '/admin/billing/plans',
    planDetail: (code = ':code') => `/admin/billing/plans/${code}`,
    subscriptions: '/admin/billing/subscriptions',
    coupons: '/admin/billing/coupons',
    couponDetail: (code = ':code') => `/admin/billing/coupons/${code}`,
    subscriptionDetail: (id = ':id') => `/admin/billing/subscriptions/${id}`,
    integrations: '/admin/integrations',
    // Each list is its own address rather than a tab remembered in component
    // state: the four are separate destinations in the System administration
    // menu, and a menu entry that cannot be linked to, bookmarked or reloaded
    // onto the same screen is not really a destination.
    referenceData: (kind = ':kind') => `/admin/reference-data/${kind}`,
    universities: '/admin/reference-data/universities',
    industries: '/admin/reference-data/industries',
    jobCategories: '/admin/reference-data/job-categories',
    skills: '/admin/reference-data/skills',
    messages: '/admin/messages',
    announcements: '/admin/announcements',
  },

  notFound: '*',
};

/** Where each kind of account starts: its dashboard, never the marketing home. */
const DASHBOARD_BY_ROLE = {
  STUDENT: paths.student.dashboard,
  COMPANY: paths.company.dashboard,
  SUPER_ADMIN: paths.admin.overview,
};

export function dashboardFor(role) {
  return DASHBOARD_BY_ROLE[role] ?? paths.home;
}
